/**
 * Curated source citations for the ROI calculator.
 *
 * Three tiers:
 *  - Tier 1: French-language links (venue owners can read these)
 *  - Tier 2: Brand-name citations only (English reports, credibility through name)
 *  - Tier 3: "En savoir plus" deep-dive reports
 */

export interface Source {
  id: string;
  label: string;
  source: string;
  url?: string;
  isEnglish?: boolean;
}

export interface BrandCitation {
  id: string;
  claim: string;
  citation: string;
}

// ---------------------------------------------------------------------------
// Tier 1 — French sources to link directly
// ---------------------------------------------------------------------------
export const FRENCH_SOURCES: Source[] = [
  {
    id: 'fft-observatoire',
    label: '850 000 joueurs, 4 100 terrains, satisfaction et freins',
    source: 'FFT — Observatoire du Padel 2025',
    url: 'https://www.fft.fr/actualites/padel-expansion-chiffres-2025-pistes-pratiquants-licencies',
  },
  {
    id: 'fft-100k',
    label: 'Croissance du nombre de licenciés padel',
    source: 'FFT — 100 000 licenciés padel',
    url: 'https://www.fft.fr/actualites/la-barre-des-100-000-licencies-padel-atteinte-2025',
  },
  {
    id: 'padelmag-rentabilite',
    label: '80 000 €/terrain/an, taux d\'occupation',
    source: 'Padel Magazine — Combien rapporte le padel',
    url: 'https://padelmagazine.fr/combien-rapporte-reellement-le-padel-en-france/',
  },
  {
    id: 'padelmag-profil',
    label: 'Démographie, comportement et freins des joueurs',
    source: 'Padel Magazine — Profil des joueurs',
    url: 'https://padelmagazine.fr/observatoire-du-padel-2025-qui-sont-vraiment-les-pratiquants-en-france-et-pourquoi-la-pratique-reste-souvent-occasionnelle/',
  },
  {
    id: 'padelmag-attentes',
    label: 'Disponibilité = critère #1, satisfaction 12.8/20',
    source: 'Padel Magazine — Attentes des clubs',
    url: 'https://padelmagazine.fr/observatoire-du-padel-2025-ce-que-les-pratiquants-attendent-des-clubs-et-la-ou-ca-coince-encore/',
  },
];

// ---------------------------------------------------------------------------
// Tier 2 — Brand-name citations (no links, credibility through name)
// ---------------------------------------------------------------------------
export const BRAND_CITATIONS: BrandCitation[] = [
  {
    id: 'playtomic-pwc',
    claim: 'Les clubs digitaux surpassent les non-digitaux de 3 à 5x en revenus et rétention',
    citation: 'Playtomic Global Padel Report 2025, produit avec PwC Strategy&',
  },
  {
    id: 'classpass',
    claim: 'Réservations de sports de raquette en hausse de 111 % en un an',
    citation: 'ClassPass Look Back Report 2025',
  },
  {
    id: 'mindbody',
    claim: 'Réduction des no-shows, comportement de réservation',
    citation: 'Mindbody, State of the Industry 2025',
  },
  {
    id: 'cochrane',
    claim: 'Les rappels SMS réduisent les no-shows de 29 à 39 %',
    citation: 'Méta-analyse Cochrane (revue systématique)',
  },
  {
    id: 'mckinsey',
    claim: '23 % du temps de travail est automatisable',
    citation: 'McKinsey Global Institute',
  },
  {
    id: 'pwc-cx',
    claim: 'Les clients sont prêts à payer 16 % de plus pour une bonne expérience',
    citation: 'PwC, Future of Customer Experience',
  },
  {
    id: 'bain-harvard',
    claim: '5 % d\'amélioration de la rétention = 25-95 % de profit en plus',
    citation: 'Bain & Company / Harvard Business School',
  },
  {
    id: 'deloitte',
    claim: 'Les entreprises digitalement matures sont 3x plus performantes',
    citation: 'Deloitte Digital Maturity Survey',
  },
];

// ---------------------------------------------------------------------------
// Tier 3 — "En savoir plus" reports (collapsible, 6 max)
// ---------------------------------------------------------------------------
export const DEEP_DIVE_REPORTS: Source[] = [
  {
    id: 'report-fft',
    label: 'Observatoire du Padel 2025',
    source: 'FFT + Union Sport & Cycle',
    url: 'https://www.fft.fr/actualites/padel-expansion-chiffres-2025-pistes-pratiquants-licencies',
  },
  {
    id: 'report-playtomic',
    label: 'Playtomic Global Padel Report 2025',
    source: 'Playtomic + PwC Strategy&',
    url: 'https://playtomic.com/global-padel-report',
    isEnglish: true,
  },
  {
    id: 'report-classpass',
    label: 'ClassPass Look Back Report 2025',
    source: 'ClassPass (Mindbody)',
    url: 'https://classpass.com/blog/2025-classpass-look-back-report/',
    isEnglish: true,
  },
  {
    id: 'report-mindbody',
    label: 'Mindbody State of the Industry 2025',
    source: 'Mindbody',
    url: 'https://www.mindbodyonline.com/business/education/research-report/2025-state-industry-report',
    isEnglish: true,
  },
  {
    id: 'report-padelmag',
    label: 'Rentabilité du padel en France',
    source: 'Padel Magazine',
    url: 'https://padelmagazine.fr/combien-rapporte-reellement-le-padel-en-france/',
  },
  {
    id: 'report-playtomic-pdf',
    label: 'Playtomic Global Padel Report 2025 — PDF complet',
    source: 'Playtomic + PwC Strategy&',
    url: 'https://www.padeladdict.com/wp-content/uploads/2025/07/PLAYTOMIC_GLOBAL-_PADEL_REPORT_2025.pdf',
    isEnglish: true,
  },
];
